/**
 * The rundown editor page (container, PLAN §6.2, D25): the live session
 * (`api/rundownEditor.ts`) in, the presentational editor out. It owns the
 * page's UI state — focus and selection, collapsed rows, the field being
 * edited, the delete confirmation — and turns the components' events into
 * patches (`lib/editor/patches.ts`), moves (`lib/editor/drop.ts`) and
 * presence (`presence.update` of the focused row and field).
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import type { EditorNotice, EditorSession } from '../api/rundownEditor.js';
import { useEditorSnapshot, useRundownEditorSession, useSession } from '../app/hooks.js';
import { DeleteRowsDialog } from '../components/editor/DeleteRowsDialog.js';
import { EditorToolbar } from '../components/editor/EditorToolbar.js';
import { PresenceAvatars } from '../components/editor/PresenceAvatars.js';
import { ReconnectBanner } from '../components/editor/ReconnectBanner.js';
import { RundownTree } from '../components/editor/RundownTree.js';
import { Button } from '../components/ui/button.js';
import { type DropTarget, planDrop } from '../lib/editor/drop.js';
import { createCommandFor, EMPTY_NAV, type NavState, navRows, pruneNav, type TreeCommand } from '../lib/editor/keyboard.js';
import { createPatch, deletePatches, initialFields, movePatches, ordForInsert, setPatch } from '../lib/editor/patches.js';
import { EMPTY_PRESENCE, presenceView } from '../lib/editor/presence.js';
import { displayName, type FlatRow, flattenTree, type TreeKind } from '../lib/editor/viewModel.js';
import { EditorInspector } from './EditorInspector.js';

function notifyToast(notice: EditorNotice): void {
  if (notice.level === 'error') toast.error(notice.message);
  else toast(notice.message);
}

/** The field a row's inline name edit writes. */
function nameField(kind: TreeKind): 'title' | 'name' {
  return kind === 'part' ? 'title' : 'name';
}

function rundownTitle(session: EditorSession, rundownId: string): string {
  const name = session.store.get(rundownId)?.f['name'];
  return typeof name === 'string' && name !== '' ? name : '(unnamed rundown)';
}

type Ui = {
  readonly collapsed: ReadonlySet<string>;
  readonly setCollapsed: (next: ReadonlySet<string>) => void;
  readonly setNav: (nav: NavState) => void;
  readonly setConfirmDelete: (ids: readonly string[] | null) => void;
};

/** What each tree command does to the session and the page's state. */
function runCommand(session: EditorSession, rundownId: string, ui: Ui, command: TreeCommand): void {
  switch (command.type) {
    case 'create': {
      const parent = command.parentId ?? rundownId;
      const ord = ordForInsert(session.store.tree, parent, command.afterId, session.writer.ordTag);
      const patch = createPatch(session.writer, rundownId, command.kind, parent, ord, initialFields(command.kind));
      session.commit([patch], true);
      if (ui.collapsed.has(parent)) ui.setCollapsed(new Set([...ui.collapsed].filter((id) => id !== parent)));
      ui.setNav({ focusedId: patch.rowId, selected: [patch.rowId], anchorId: patch.rowId, editingId: patch.rowId });
      return;
    }
    case 'delete':
      ui.setConfirmDelete(command.ids);
      return;
    case 'undo':
      session.undo();
      return;
    case 'redo':
      session.redo();
      return;
    case 'toggle-collapse': {
      const next = new Set(ui.collapsed);
      if (next.has(command.id)) next.delete(command.id);
      else next.add(command.id);
      ui.setCollapsed(next);
      return;
    }
  }
}

function namesOf(rows: readonly FlatRow[], ids: readonly string[]): string[] {
  return ids.map((id) => {
    const row = rows.find((r) => r.id === id);
    return row === undefined ? id : displayName(row.view);
  });
}

export function RundownEditorView({ rundownId }: { readonly rundownId: string }) {
  const session = useRundownEditorSession(rundownId, notifyToast);
  const snap = useEditorSnapshot(session);
  const self = useSession();
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [rawNav, setNav] = useState<NavState>(EMPTY_NAV);
  const [field, setField] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<readonly string[] | null>(null);

  const flat = useMemo(() => flattenTree(snap.rows.tree, rundownId, collapsed), [snap.rows, rundownId, collapsed]);
  const rowsNav = useMemo(() => navRows(flat.rows), [flat]);
  const nav = pruneNav(rawNav, rowsNav);
  const presence = useMemo(() => (self === null ? EMPTY_PRESENCE : presenceView(snap.members, self.userId)), [snap.members, self]);
  const displayOrder = useMemo(() => flat.rows.map((r) => r.id), [flat]);
  const focusedFlat = flat.rows.find((r) => r.id === nav.focusedId) ?? null;
  const focusedRow = focusedFlat === null ? null : session.store.get(focusedFlat.id);
  const focusedNav = rowsNav.find((r) => r.id === nav.focusedId) ?? null;

  // Presence follows focus: the row, and the field being edited (inline name, inspector field, script).
  const editingField = nav.editingId !== null && focusedFlat !== null ? nameField(focusedFlat.view.kind) : field;
  useEffect(() => {
    session.setPresence({ rowId: nav.focusedId, field: nav.focusedId === null ? null : editingField, activity: editingField === null ? 'viewing' : 'editing' });
  }, [session, nav.focusedId, editingField]);

  if (snap.status === 'failed') {
    return (
      <div className="mx-auto grid max-w-xl gap-4 p-6" role="alert" data-testid="editor-failed">
        <p className="text-sm text-destructive">Could not open the rundown: {snap.error}</p>
        <Button variant="outline" onClick={() => session.retry()} className="w-fit">
          Retry
        </Button>
      </div>
    );
  }

  const ui: Ui = { collapsed, setCollapsed, setNav, setConfirmDelete };
  const onCommand = (command: TreeCommand) => runCommand(session, rundownId, ui, command);
  const createFor = (kind: TreeKind) => createCommandFor(kind, focusedNav, rowsNav);
  const planFor = (ids: readonly string[], target: DropTarget) => planDrop(session.store.tree, displayOrder, ids, target, session.writer.ordTag);
  const totalDuration = flat.rows.reduce((sum, r) => (r.view.kind === 'segment' && !r.lost ? sum + r.view.totalDuration : sum), 0);

  return (
    <div className="flex h-full flex-col" data-testid="rundown-editor">
      <EditorToolbar
        title={snap.status === 'loading' ? 'Loading…' : rundownTitle(session, rundownId)}
        totalDuration={totalDuration}
        canCreate={{ segment: snap.status === 'ready', part: createFor('part') !== null, piece: createFor('piece') !== null }}
        canUndo={snap.canUndo}
        canRedo={snap.canRedo}
        canDelete={nav.selected.length > 0}
        onCreate={(kind) => {
          const command = createFor(kind);
          if (command !== null) onCommand(command);
        }}
        onUndo={() => session.undo()}
        onRedo={() => session.redo()}
        onDelete={() => setConfirmDelete(displayOrder.filter((id) => nav.selected.includes(id)))}
        presence={<PresenceAvatars users={presence.users} />}
      />
      <ReconnectBanner connection={snap.status === 'loading' ? 'connecting' : snap.connection} retryInMs={snap.retryInMs} pending={snap.pending} />
      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1">
          <RundownTree
            rows={flat.rows}
            presence={presence.byRow}
            nav={nav}
            onNavChange={setNav}
            onCommand={onCommand}
            onRename={(id, value) => {
              const row = flat.rows.find((r) => r.id === id);
              if (row !== undefined) session.commit([setPatch(session.writer, id, { [nameField(row.view.kind)]: value })], true);
            }}
            canDrop={(ids, target) => planFor(ids, target) !== null}
            onDrop={(ids, target) => {
              const moves = planFor(ids, target);
              if (moves !== null) session.commit(movePatches(session.writer, moves), true);
            }}
          />
        </div>
        <div className="w-96 shrink-0 overflow-auto border-l bg-card">
          <EditorInspector
            session={session}
            focused={focusedFlat === null || focusedRow === null ? null : { row: focusedRow, view: focusedFlat.view }}
            presence={nav.focusedId === null ? [] : (presence.byRow.get(nav.focusedId) ?? [])}
            onFocusField={setField}
          />
        </div>
      </div>
      <DeleteRowsDialog
        open={confirmDelete !== null}
        names={namesOf(flat.rows, confirmDelete ?? [])}
        onOpenChange={(open) => {
          if (!open) setConfirmDelete(null);
        }}
        onConfirm={() => {
          if (confirmDelete !== null) session.commit(deletePatches(session.writer, confirmDelete), true);
          setConfirmDelete(null);
        }}
      />
    </div>
  );
}
